import { TWO_FACTOR } from '../constants/actions';
import { fromPath } from '../selectors/router';
import { apiActionCreator, extractErrorMessage } from './helpers';
import { users, twoFactor, loginWith2FARecovery } from '../services/api';
import { LOGIN_PATH, ROOT_PATH, TWO_FACTOR_RECOVERY_PATH } from '../constants/routes';
import { storeToken } from '../services/local.storage';
import * as HTTP_CODES from '../constants/http.codes';
import {
    emailSelector,
    rememberMeSelector,
    twoFactorSetupTokenSelector,
    twoFactorTempCredentialsSelector,
    twoFactorTempLoginTokenSelector,
    twoFactorRememberMeSelector
} from '../selectors/authentication';
import { push } from 'react-router-redux';
import { omit } from 'lodash';
import { openFlash } from './global';

export function inputTwoFactor(value, key) {
    return {
        type: TWO_FACTOR.INPUT,
        key: key,
        value: value
    };
}

export function resetTwoFactor() {
    return {
        type: TWO_FACTOR.RESET
    };
}

export function fetchTwoFactorSetup() {
    return async (dispatch, getState) => {
        const setupToken = twoFactorSetupTokenSelector(getState());
        if (!setupToken) {
            dispatch(push(LOGIN_PATH));
            return;
        }

        dispatch({ type: TWO_FACTOR.FETCH_START });

        await apiActionCreator(
            dispatch,
            () => twoFactor.setup(setupToken),
            response => {
                dispatch({
                    type: TWO_FACTOR.FETCH_OK,
                    qrCode: response.qrCode,
                    secret: response.secret
                });
            },
            {
                allErrors: error => {
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: error?.message || 'Failed to load 2FA setup. Please try logging in again.'
                    });
                    dispatch(push(LOGIN_PATH));
                }
            }
        );
    };
}

export function verifyTwoFactorSetup(code) {
    return async (dispatch, getState) => {
        const setupToken = twoFactorSetupTokenSelector(getState());
        if (!setupToken) {
            dispatch(push(LOGIN_PATH));
            return;
        }

        dispatch({ type: TWO_FACTOR.SUBMIT });

        await apiActionCreator(
            dispatch,
            () => twoFactor.verify(setupToken, code),
            async response => {
                if (response.verified && response.token) {
                    storeToken(response.token, twoFactorRememberMeSelector(getState()));

                    if (response.recoveryCodes && response.recoveryCodes.length > 0) {
                        dispatch({
                            type: TWO_FACTOR.SHOW_RECOVERY_CODES,
                            recoveryCodes: response.recoveryCodes
                        });
                    }

                    await apiActionCreator(
                        dispatch,
                        () => users.profile(),
                        profileResponse => {
                            storeToken(profileResponse.token, twoFactorRememberMeSelector(getState()));
                            dispatch({
                                type: TWO_FACTOR.SUCCESS,
                                user: { ...omit(profileResponse, 'token') }
                            });
                            if (!response.recoveryCodes || response.recoveryCodes.length === 0) {
                                dispatch(push(ROOT_PATH));
                            }
                        },
                        {
                            allErrors: () => {
                                dispatch({
                                    type: TWO_FACTOR.SUCCESS,
                                    user: { email: emailSelector(getState()) }
                                });
                                if (!response.recoveryCodes || response.recoveryCodes.length === 0) {
                                    dispatch(push(ROOT_PATH));
                                }
                            }
                        }
                    );
                } else {
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: 'Invalid code.'
                    });
                }
            },
            {
                allErrors: error => {
                    const errorMessage = extractErrorMessage(error);
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: errorMessage || 'Invalid code.'
                    });
                }
            }
        );
    };
}

export function submitLoginWith2FA(code) {
    return async (dispatch, getState) => {
        const tempCredentials = twoFactorTempCredentialsSelector(getState());
        const tempLoginToken = twoFactorTempLoginTokenSelector(getState());
        if (!tempCredentials || !tempLoginToken) {
            dispatch(push(LOGIN_PATH));
            return;
        }

        dispatch({ type: TWO_FACTOR.SUBMIT });

        await apiActionCreator(
            dispatch,
            () => users.loginWith2FA(tempLoginToken, { twoFactorToken: code }),
            response => {
                if (response.token) {
                    storeToken(response.token, twoFactorRememberMeSelector(getState()));
                    dispatch({
                        type: TWO_FACTOR.SUCCESS,
                        user: { ...response, email: tempCredentials.email }
                    });
                    const redirect = fromPath(getState()) || ROOT_PATH;
                    dispatch(push(redirect));
                } else {
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: 'Invalid code.'
                    });
                }
            },
            {
                allErrors: error => {
                    const errorMessage = extractErrorMessage(error);
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: errorMessage || 'Invalid code.'
                    });
                }
            }
        );
    };
}

export function cancelTwoFactor() {
    return dispatch => {
        dispatch({ type: TWO_FACTOR.RESET });
        dispatch(push(LOGIN_PATH));
    };
}

export function toggleRecoveryMode() {
    return {
        type: TWO_FACTOR.TOGGLE_RECOVERY_MODE
    };
}

export function hideRecoveryCodes() {
    return dispatch => {
        dispatch({ type: TWO_FACTOR.HIDE_RECOVERY_CODES });
        dispatch(push(ROOT_PATH));
    };
}

export function submitLoginWithRecoveryCode(recoveryCode) {
    return async (dispatch, getState) => {
        const tempCredentials = twoFactorTempCredentialsSelector(getState());
        const tempLoginToken = twoFactorTempLoginTokenSelector(getState());
        if (!tempCredentials || !tempLoginToken) {
            dispatch(push(LOGIN_PATH));
            return;
        }

        dispatch({ type: TWO_FACTOR.SUBMIT });

        await apiActionCreator(
            dispatch,
            () => loginWith2FARecovery(tempLoginToken, recoveryCode),
            response => {
                if (response.token) {
                    storeToken(response.token, twoFactorRememberMeSelector(getState()));
                    dispatch({
                        type: TWO_FACTOR.RECOVERY_LOGIN_SUCCESS,
                        user: { ...response, email: tempCredentials.email },
                        remainingRecoveryCodes: response.remainingRecoveryCodes
                    });
                    if (response.usedRecoveryCode && response.remainingRecoveryCodes !== undefined) {
                        if (response.remainingRecoveryCodes <= 3) {
                            dispatch(
                                openFlash(
                                    `Warning: You have only ${response.remainingRecoveryCodes} recovery codes remaining.`,
                                    'warning'
                                )
                            );
                        } else {
                            dispatch(
                                openFlash(
                                    `You have ${response.remainingRecoveryCodes} recovery codes remaining.`,
                                    'info'
                                )
                            );
                        }
                    }

                    dispatch(push(TWO_FACTOR_RECOVERY_PATH));
                } else {
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: 'Invalid code.'
                    });
                }
            },
            {
                allErrors: error => {
                    const errorMessage = extractErrorMessage(error);
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: errorMessage || 'Invalid code.'
                    });
                },
                [HTTP_CODES.UNAUTHORIZED_CODE]: error => {
                    const errorMessage = extractErrorMessage(error);
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: errorMessage || 'Invalid code.'
                    });
                },
                [HTTP_CODES.UNPROCESSABLE_ENTITY_CODE]: error => {
                    const errorMessage = extractErrorMessage(error);
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: errorMessage || 'Invalid code format.'
                    });
                }
            }
        );
    };
}

export function fetchTwoFactorStatus() {
    return async dispatch => {
        await apiActionCreator(
            dispatch,
            () => twoFactor.status(),
            response => {
                dispatch({
                    type: TWO_FACTOR.STATUS_FETCHED,
                    remainingRecoveryCodes: response.remainingRecoveryCodes,
                    enabled: response.enabled
                });
            },
            {
                allErrors: () => {}
            }
        );
    };
}

export function initiate2FAReset() {
    return async dispatch => {
        dispatch({ type: TWO_FACTOR.RESET_2FA_START });

        await apiActionCreator(
            dispatch,
            () => twoFactor.reset(),
            response => {
                if (response.qrCode && response.secret) {
                    dispatch({
                        type: TWO_FACTOR.RESET_2FA_OK,
                        qrCode: response.qrCode,
                        secret: response.secret
                    });
                } else {
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: 'Invalid code.'
                    });
                }
            },
            {
                allErrors: error => {
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: errorMessage || 'Invalid code.'
                    });
                }
            }
        );
    };
}

export function verify2FAReset(code) {
    return async dispatch => {
        dispatch({ type: TWO_FACTOR.SUBMIT });

        await apiActionCreator(
            dispatch,
            () => twoFactor.verifyReset(code),
            response => {
                if (response.verified) {
                    if (response.recoveryCodes && response.recoveryCodes.length > 0) {
                        dispatch({
                            type: TWO_FACTOR.SHOW_RECOVERY_CODES,
                            recoveryCodes: response.recoveryCodes
                        });
                    }
                    dispatch({ type: TWO_FACTOR.RESET_2FA_CANCEL });
                    dispatch(openFlash('Two-factor authentication has been reset successfully.', 'success'));
                } else {
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: 'Invalid code.'
                    });
                }
            },
            {
                allErrors: error => {
                    const errorMessage = extractErrorMessage(error);
                    dispatch({
                        type: TWO_FACTOR.FAILED,
                        error: errorMessage || 'Invalid code.'
                    });
                }
            }
        );
    };
}

export function cancel2FAReset() {
    return {
        type: TWO_FACTOR.RESET_2FA_CANCEL
    };
}
